import { Button } from 'react-aria-components';

export const paginationItemStyle = `
  flex h-12 w-12 items-center justify-center rounded-full border border-solid-gray-200 text-oln-16N-100 text-blue-1000
  hover:enabled::border-blue-900 hover:bg-blue-50
  focus-visible:outline-2 focus-visible:outline-focus-yellow
  active:enabled:active:border-blue-900 active:bg-blue-50
  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-solid-gray-200 disabled:hover:bg-white
`;

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
};

export const Pagination = ({ currentPage, totalPages, onPageChange }: PaginationProps) => {
  return (
    <nav className="flex gap-2">
      <PaginationFirst
        aria-label="最初のページ"
        onPageChange={() => onPageChange(1)}
        disabled={currentPage === 1}
      />
      <PaginationPrev
        aria-label="前のページ"
        onPageChange={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
      <PaginationCurrent
        current={currentPage}
        max={totalPages}
      />
      <PaginationNext
        aria-label="次のページ"
        onPageChange={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
      <PaginationLast
        aria-label="最後のページ"
        onPageChange={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    </nav>
  );
};

type PaginationItemProps = {
  onPageChange: () => void;
  disabled?: boolean;
};

export const PaginationFirst = ({ onPageChange, disabled }: PaginationItemProps) => {
  return (
    <Button
      className={`${paginationItemStyle}`}
      onPress={onPageChange}
      isDisabled={disabled}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          fill="white"
        />
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          stroke="#CCCCCC"
        />
        <path
          d="M30.33 31L31 30.33L24.67 24L31 17.67L30.33 17L23.33 24L30.33 31Z"
          fill="#264AF4"
        />
        <rect
          width="1"
          height="14"
          transform="matrix(1 8.74228e-08 8.74228e-08 -1 18 31)"
          fill="#264AF4"
        />
      </svg>
    </Button>
  );
};

export const PaginationPrev = ({ onPageChange, disabled }: PaginationItemProps) => {
  return (
    <Button
      className={`${paginationItemStyle}`}
      onPress={onPageChange}
      isDisabled={disabled}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          fill="white"
        />
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          stroke="#CCCCCC"
        />
        <path
          d="M27.33 31L28 30.33L21.67 24L28 17.67L27.33 17L20.33 24L27.33 31Z"
          fill="#264AF4"
        />
      </svg>
    </Button>
  );
};

export const PaginationLast = ({ onPageChange, disabled }: PaginationItemProps) => {
  return (
    <Button
      className={`${paginationItemStyle}`}
      onPress={onPageChange}
      isDisabled={disabled}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          fill="white"
        />
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          stroke="#CCCCCC"
        />
        <path
          d="M17.67 31L17 30.33L23.33 24L17 17.67L17.67 17L24.67 24L17.67 31Z"
          fill="#264AF4"
        />
        <rect
          x="30"
          y="31"
          width="1"
          height="14"
          transform="rotate(180 30 31)"
          fill="#264AF4"
        />
      </svg>
    </Button>
  );
};

export const PaginationNext = ({ onPageChange, disabled }: PaginationItemProps) => {
  return (
    <Button
      className={`${paginationItemStyle}`}
      onPress={onPageChange}
      isDisabled={disabled}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          fill="white"
        />
        <rect
          x="0.5"
          y="0.5"
          width="47"
          height="47"
          rx="23.5"
          stroke="#CCCCCC"
        />
        <path
          d="M20.67 31L20 30.33L26.33 24L20 17.67L20.67 17L27.67 24L20.67 31Z"
          fill="#264AF4"
        />
      </svg>
    </Button>
  );
};

type PaginationCurrentProps = {
  current: number;
  max: number;
};

export const PaginationCurrent = (props: PaginationCurrentProps) => {
  const { current, max } = props;

  return (
    <span className="flex items-center justify-center">
      <span className="flex gap-1">
        <span aria-current="page">{current}</span>
        <span>/</span>
        <span>{max}</span>
      </span>
    </span>
  );
};
